import React, { useRef, useState } from 'react';
import { X, Upload, ChevronLeft, Check, Image as ImageIcon, Film, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { IMG } from '../mock';

const notify = (msg) => toast.success(msg, { style: { background: '#0f1622', color: '#fff', border: '1px solid #1c2634' } });

const FILTERS = [
  { k: 'Original', v: 'none' },
  { k: 'Vivid', v: 'saturate(1.6) contrast(1.1)' },
  { k: 'Warm', v: 'sepia(0.3) saturate(1.4)' },
  { k: 'Cool', v: 'hue-rotate(-12deg) saturate(1.2) brightness(1.05)' },
  { k: 'Mono', v: 'grayscale(1) contrast(1.05)' },
  { k: 'Fade', v: 'contrast(0.9) brightness(1.1) saturate(0.8)' },
];

const SAMPLES = [IMG.P.beach1, IMG.P.city, IMG.P.mountain, IMG.P.coffee, IMG.P.sunset, IMG.P.selfie, IMG.P.dog, IMG.P.beach2];
const THUMBS = [IMG.P.sunset, IMG.P.mountain, IMG.P.selfie, IMG.P.city];

export default function MediaStudio({ mode = 'photo', onClose }) {
  const [step, setStep] = useState('pick');
  const [src, setSrc] = useState(null);
  const [isVideo, setIsVideo] = useState(mode === 'video');
  const [filter, setFilter] = useState('none');
  const [thumb, setThumb] = useState(0);
  const [caption, setCaption] = useState('');
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSrc(URL.createObjectURL(f));
    setIsVideo(f.type.startsWith('video'));
    setStep('edit');
  };
  const pickSample = (u) => { setSrc(u); setIsVideo(false); setStep('edit'); };
  const share = () => { onClose(); notify(isVideo ? 'Video posted' : 'Photo posted'); };

  return (
    <div className="fixed inset-0 z-[68] bg-[#05070c] flex flex-col max-w-[440px] mx-auto">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#141c28]">
        <button onClick={step === 'edit' ? () => setStep('pick') : onClose}>
          {step === 'edit' ? <ChevronLeft size={24} className="text-white" /> : <X size={24} className="text-white" />}
        </button>
        <h3 className="font-display text-base font-bold text-white">{step === 'edit' ? 'Edit' : `New ${mode === 'video' ? 'Video' : 'Photo'}`}</h3>
        {step === 'edit' && (
          <button onClick={share} className="ml-auto text-[14px] font-bold text-[#2f8bff]">Share</button>
        )}
      </div>

      {step === 'pick' ? (
        <div className="flex-1 overflow-y-auto app-scroll p-4">
          <button onClick={() => fileRef.current?.click()} className="w-full py-8 rounded-2xl border-2 border-dashed border-[#2a3547] flex flex-col items-center gap-2 active:scale-[0.99] transition-transform">
            <div className="w-12 h-12 rounded-full bg-[#122a3a] flex items-center justify-center">
              {mode === 'video' ? <Film size={22} className="text-[#2f8bff]" /> : <Upload size={22} className="text-[#2f8bff]" />}
            </div>
            <span className="text-[14px] font-semibold text-white">Upload {mode === 'video' ? 'a video' : 'a photo'}</span>
            <span className="text-[12px] text-[#5c6675]">Tap to browse your device</span>
          </button>
          <input ref={fileRef} type="file" accept={mode === 'video' ? 'video/*' : 'image/*'} onChange={onFile} className="hidden" />

          <p className="text-[13px] text-[#8b95a5] mt-5 mb-2">Or start from a sample</p>
          <div className="grid grid-cols-3 gap-1.5">
            {SAMPLES.map((u) => (
              <button key={u} onClick={() => pickSample(u)} className="aspect-square rounded-lg overflow-hidden active:scale-95 transition-transform">
                <img src={u} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto app-scroll">
          <div className="bg-black flex items-center justify-center" style={{ minHeight: 300 }}>
            {isVideo ? (
              <video src={src} controls playsInline className="w-full max-h-[46vh] object-contain" style={{ filter }} />
            ) : (
              <img src={src} alt="" className="w-full max-h-[46vh] object-contain" style={{ filter }} />
            )}
          </div>

          <div className="p-4">
            <p className="flex items-center gap-1.5 text-[13px] font-semibold text-white mb-2"><Sparkles size={14} className="text-[#2f8bff]" /> Filters</p>
            <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1">
              {FILTERS.map((f) => (
                <button key={f.k} onClick={() => setFilter(f.v)} className="shrink-0 flex flex-col items-center gap-1">
                  <div className={`w-16 h-16 rounded-lg overflow-hidden ring-2 ${filter === f.v ? 'ring-[#2f8bff]' : 'ring-transparent'}`}>
                    <img src={src} alt="" className="w-full h-full object-cover" style={{ filter: f.v }} />
                  </div>
                  <span className={`text-[11px] ${filter === f.v ? 'text-[#2f8bff] font-semibold' : 'text-[#8b95a5]'}`}>{f.k}</span>
                </button>
              ))}
            </div>

            {isVideo && (
              <>
                <p className="flex items-center gap-1.5 text-[13px] font-semibold text-white mt-4 mb-2"><ImageIcon size={14} className="text-[#2f8bff]" /> Cover thumbnail</p>
                <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1">
                  {THUMBS.map((u, i) => (
                    <button key={u} onClick={() => setThumb(i)} className={`shrink-0 w-16 h-20 rounded-lg overflow-hidden ring-2 ${thumb === i ? 'ring-[#2f8bff]' : 'ring-transparent'}`}>
                      <img src={u} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </>
            )}

            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Write a caption..."
              className="w-full mt-4 bg-[#0c1420] rounded-xl px-4 py-3 text-[14px] text-white placeholder:text-[#5c6675] outline-none ring-1 ring-[#1c2634] focus:ring-[#2f8bff]"
            />
            <button onClick={share} className="w-full mt-4 py-3.5 rounded-xl bg-[#2f8bff] text-white text-[15px] font-bold active:scale-[0.98] transition-transform">
              Share {isVideo ? 'video' : 'photo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
